import React from "react";
import Link from "gatsby-link";
import styled, { ThemeProvider } from "styled-components";
import { shade, rgba } from "polished";
import theme from "../theme.js";
import invertTheme from "../invertTheme.js";
import { Box, Flex } from "rebass";
import { Button, H3, H4, Text } from "components";
import OverlayImage from "./OverlayImage.js";

const Container = styled(Flex)`
  position: relative;
  cursor: pointer;
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
  opacity: 0;
  animation: resource-card-fade-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;

  @keyframes resource-card-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    transform: translateY(-4px);

    ${Button} {
      color: ${(props) => props.theme.colors.grey[0]};
      background-color: ${(props) => props.theme.colors[props.color]};
      border-color: ${(props) => props.theme.colors[props.color]};
    }
  }

  &:active {
    box-shadow: none;
    transform: translateY(-2px);

    ${Button} {
      color: ${(props) => props.theme.colors[props.color]};
      background-color: ${(props) => props.theme.colors.grey[0]};
      border-color: ${(props) => props.theme.colors.grey[0]};
    }
  }

  *::selection {
    color: ${(props) =>
      props.theme.colors[props.color] &&
      shade(0.75, props.theme.colors[props.color])};
    background-color: ${(props) => props.theme.colors.grey[0]};
  }
`;

const Metadata = styled(H4)`
  position: relative;
  min-height: 36px;
`;

const Title = styled(H3)`
  position: relative;
  min-height: none;

  @media (min-width: 35em) {
    min-height: 176px;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.333;
  /* background-color: ${(props) => props.theme.colors[props.cardColor]}; */
  background-color: ${(props) => props.theme.colors.grey[2]};

  &:not(:lang(ar)) {
    &:after {
      /* background-image: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to right, " +
          rgba(props.theme.colors[props.cardColor], 0.9) +
          ", " +
          rgba(props.theme.colors[props.cardColor], 0.75) +
          ")"}; */
      background-image: ${(props) =>
        props.theme.colors.grey[2] &&
        "linear-gradient(to right, " +
          rgba(props.theme.colors.grey[2], 0.9) +
          ", " +
          rgba(props.theme.colors.grey[2], 0.75) +
          ")"};
    }
  }

  &:lang(ar) {
    &:after {
      /* background-image: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to left, " +
          rgba(props.theme.colors[props.cardColor], 0.9) +
          ", " +
          rgba(props.theme.colors[props.cardColor], 0.75) +
          ")"}; */
      background-image: ${(props) =>
        props.theme.colors.grey[2] &&
        "linear-gradient(to left, " +
          rgba(props.theme.colors.grey[2], 0.9) +
          ", " +
          rgba(props.theme.colors.grey[2], 0.75) +
          ")"};
    }
  }

  &:after {
    position: absolute;
    content: "";
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.2);
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    max-width: 672px;
    height: 100%;
    filter: grayscale(1);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    /* Fallback */
    background-image: ${(props) =>
      "url(" + (props.backgroundImage ? props.backgroundImage[2] : "") + ")"};

    /* Webkit fallback */
    background-image: ${(props) =>
      "-webkit-image-set(url(" +
      (props.backgroundImage ? props.backgroundImage[2] : "") +
      ") 1x, url(" +
      (props.backgroundImage ? props.backgroundImage[1] : "") +
      ") 2x)"};

    /* Regular imageset */
    background-image: ${(props) =>
      "image-set(url(" +
      (props.backgroundImage ? props.backgroundImage[2] : "") +
      ") 1x, url(" +
      (props.backgroundImage ? props.backgroundImage[1] : "") +
      ") 2x)"};

    @media (min-width: 52em) {
      /* Fallback */
      background-image: ${(props) =>
        "url(" + (props.backgroundImage ? props.backgroundImage[1] : "") + ")"};

      /* Webkit fallback */
      background-image: ${(props) =>
        "-webkit-image-set(url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 1x, url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 2x, url(" +
        (props.backgroundImage ? props.backgroundImage[0] : "") +
        ") 3x)"};

      /* Regular imageset */
      background-image: ${(props) =>
        "image-set(url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 1x, url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 2x, url(" +
        (props.backgroundImage ? props.backgroundImage[0] : "") +
        ") 3x)"};
    }

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      /* background-image: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to bottom, " +
          (props.cardColor
            ? rgba(props.theme.colors[props.cardColor], 0.66)
            : "") +
          ", " +
          (props.cardColor
            ? rgba(props.theme.colors[props.cardColor], 0.66)
            : "") +
          "), url(" +
          (props.backgroundImage ? props.backgroundImage[2] : "") +
          ")"}; */
      background-image: ${(props) =>
        props.theme.colors.grey[2] &&
        "linear-gradient(to bottom, " +
          (props.cardColor ? rgba(props.theme.colors.grey[2], 0.66) : "") +
          ", " +
          (props.cardColor ? rgba(props.theme.colors.grey[2], 0.66) : "") +
          "), url(" +
          (props.backgroundImage ? props.backgroundImage[2] : "") +
          ")"};
    }
  }
`;

const TopBar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 5;
  width: 100%;
  height: 8px;
  background-color: ${(props) => props.theme.colors[props.cardColor]};
`;

const ColorCoded = styled.span`
  color: ${(props) => props.theme.colors[props.cardColor]};
`;

const determineLabel = function (region) {
  let label = "BIC";
  if (region === "middle-east-and-north-africa") {
    label = "Middle East and North Africa";
  }
  if (region === "oriente-medio-y-norte-de-africa") {
    label = "Oriente Medio y Norte de África";
  }
  if (region === "الشرق-الاوسط-وشمات-افريقيا") {
    label = "الشرق الأوسط وشمات أفريقيا";
  }
  if (region === "africa") {
    label = "Africa";
  }
  if (region === "africa1") {
    label = "África";
  }
  if (region === "افريقيا") {
    label = "أفريقيا";
  }
  if (region === "latin-america") {
    label = "Latin America";
  }
  if (region === "america-latina-y-el-caribe") {
    label = "América Latina y el Caribe";
  }
  if (region === "امريكا-اللاتينية") {
    label = "أمريكا  اللاتينية";
  }
  if (region === "asia") {
    label = "Asia";
  }
  if (region === "asia1") {
    label = "Asia";
  }
  if (region === "اسيا") {
    label = "آسيا";
  }
  if (region === "europe--central-asia") {
    label = "Europe & Central Asia";
  }
  if (region === "europa-y-asia-central") {
    label = "Europa y Asia Central";
  }
  if (region === "اوروبا-واسيا-الوسطى") {
    label = "أوروبا وآسيا الوسطى";
  }
  return label;
};

const determineColor = function (region) {
  let color = "violet";
  if (
    region === "middle-east-and-north-africa" ||
    region === "oriente-medio-y-norte-de-africa" ||
    region === "الشرق-الاوسط-وشمات-افريقيا"
  ) {
    color = "orange";
  }
  if (region === "africa" || region === "africa1" || region === "افريقيا") {
    color = "green";
  }
  if (
    region === "latin-america" ||
    region === "america-latina-y-el-caribe" ||
    region === "امريكا-اللاتينية"
  ) {
    color = "blue";
  }
  if (region === "asia" || region === "asia1" || region === "اسيا") {
    color = "pink";
  }
  if (
    region === "europe--central-asia" ||
    region === "europa-y-asia-central" ||
    region === "اوروبا-واسيا-الوسطى"
  ) {
    color = "yellow";
  }
  return color;
};

const truncateText = (text) => {
  if (text.length > 90) {
    let textArray = text.split(" ");
    let textBuildup = "";
    let position = 0;
    while (textBuildup.length < 90) {
      textBuildup += textArray[position] + " ";
      position++;
    }
    if (
      textBuildup[textBuildup.length - 2] !== "?" &&
      textBuildup[textBuildup.length - 2] !== "!" &&
      textBuildup[textBuildup.length - 2] !== "."
    ) {
      text = textBuildup.slice(0, textBuildup.length - 1) + "...";
    }
  }
  return text;
};

const Card = (props) => {
  if (props.object) {
    return (
      <ThemeProvider
        theme={
          determineColor(
            props.object.data.region
              ? props.object.data.region.uid
              : props.object.type
          ) === "lightgrey"
            ? theme
            : invertTheme
        }
      >
        <Container
          is={Link}
          to={
            props.object.type === "member"
              ? "/" + props.object.lang + "/team/" + props.object.uid
              : props.object.type === "update"
              ? "/" + props.object.lang + "/update/" + props.object.uid
              : props.object.type === "projec"
              ? "/" + props.object.lang + "/project/" + props.object.uid
              : props.object.type === "region"
              ? "/" + props.object.lang + "/region/" + props.object.uid
              : props.object.type === "institution"
              ? "/" + props.object.lang + "/institution/" + props.object.uid
              : "/" + props.object.lang + "/" + props.object.type
          }
          // onClick={() => {
          //   if (props.object.type === "projec") {
          //     document.body.style.background =
          //       theme.colors[determineColor(props.object.data.region.uid)];
          //   }
          // }}
          bg={determineColor(
            props.object.data.region
              ? props.object.type === "update"
                ? props.object.type
                : props.object.data.region.uid
              : props.object.type === "region"
              ? props.object.uid
              : props.object.type
          )}
          bg="grey.2"
          color={determineColor(
            props.object.data.region
              ? props.object.type === "update"
                ? props.object.type
                : props.object.data.region.uid
              : props.object.type === "region"
              ? props.object.uid
              : props.object.type
          )}
          title={
            props.object.type === "update"
              ? "Update"
              : props.object.data.title
              ? props.object.data.title[0].text
              : ""
          }
          aria-label={
            props.object.data.title ? props.object.data.title[0].text : ""
          }
          flexDirection="column"
          align="flex-start"
          px={4}
          py={5}
        >
          <TopBar
            cardColor={determineColor(
              props.object.data.region
                ? props.object.type === "update"
                  ? props.object.type
                  : props.object.data.region.uid
                : props.object.type === "region"
                ? props.object.uid
                : props.object.type
            )}
          />
          <ImageContainer
            backgroundImage={
              props.object.data.header_image
                ? [
                    props.object.data.header_image.half.url,
                    props.object.data.header_image.quarter.url,
                    props.object.data.header_image.quarter.url,
                  ]
                : ""
            }
            cardColor={determineColor(
              props.object.data.region
                ? props.object.type === "update"
                  ? props.object.type
                  : props.object.data.region.uid
                : props.object.type === "region"
                ? props.object.uid
                : props.object.type
            )}
            bg={determineColor(
              props.object.data.region
                ? props.object.type === "update"
                  ? props.object.type
                  : props.object.data.region.uid
                : props.object.type === "region"
                ? props.object.uid
                : props.object.type
            )}
            bg="grey.2"
          />
          {/* <OverlayImage
            backgroundImage={
              props.object.data.header_image
                ? [
                    props.object.data.header_image.url,
                    props.object.data.header_image.half
                      ? props.object.data.header_image.half.url
                      : "",
                    props.object.data.header_image.quarter
                      ? props.object.data.header_image.quarter.url
                      : ""
                  ]
                : undefined
            }
            imageColor={determineColor(
              props.object.data.region
                ? props.object.type === "update"
                  ? props.object.type
                  : props.object.data.region.uid
                : props.object.type === "region"
                  ? props.object.uid
                  : props.object.type
            )}
          /> */}

          <Metadata is="div" mb={5}>
            <ColorCoded
              children={
                props.object.type === "update" || props.object.type === "region"
                  ? "BIC"
                  : determineLabel(
                      props.object.data.region
                        ? props.object.data.region.uid
                        : ""
                    )
              }
              cardColor={determineColor(
                props.object.data.region
                  ? props.object.type === "update"
                    ? props.object.type
                    : props.object.data.region.uid
                  : props.object.type === "region"
                  ? props.object.uid
                  : props.object.type
              )}
            />
            {" | "}
            {props.object.type === "member"
              ? props.object.lang === "en-us"
                ? "Staff Feature"
                : props.object.lang === "es-mx"
                ? "Staff"
                : "تسليط الضوء على موظفي المركز"
              : props.object.type === "update"
              ? props.object.lang === "en-us"
                ? "Update"
                : props.object.lang === "es-mx"
                ? "Noticia"
                : "تحديث"
              : props.object.type === "projec"
              ? props.object.lang === "en-us"
                ? "Project"
                : props.object.lang === "es-mx"
                ? "Proyecto"
                : "مشروع"
              : props.object.type === "region"
              ? props.windowLang === "en-us"
                ? "Region"
                : props.windowLang === "es-mx"
                ? "Regione"
                : "مشروع"
              : props.object.type === "institution"
              ? props.windowLang === "en-us"
                ? "Institution"
                : props.windowLang === "es-mx"
                ? "Institucione"
                : "مؤسسات"
              : props.object.type === "team"
              ? props.object.lang === "en-us"
                ? "Team"
                : props.object.lang === "es-mx"
                ? "Equipo"
                : "الفريق"
              : props.object.type === "resources"
              ? props.object.lang === "en-us"
                ? "Resource"
                : props.object.lang === "es-mx"
                ? "Recurso"
                : "مورد"
              : props.object.type === "support"
              ? props.object.lang === "en-us"
                ? "Support"
                : props.object.lang === "es-mx"
                ? "Súmate"
                : "الدعم"
              : props.object.type === "careers"
              ? props.object.lang === "en-us"
                ? "Careers"
                : props.object.lang === "es-mx"
                ? "Empleo"
                : "وظائف"
              : props.object.type === "contact"
              ? props.object.lang === "en-us"
                ? "Contact"
                : props.object.lang === "es-mx"
                ? "Contacto"
                : "اتصل"
              : props.object.type === "about"
              ? props.object.lang === "en-us"
                ? "About"
                : props.object.lang === "es-mx"
                ? "Sobre BIC"
                : "عن نحن"
              : ""}
          </Metadata>
          <Box>
            <Title
              children={
                props.object.type === "member"
                  ? props.object.data.description[0]
                    ? truncateText(props.object.data.description[0].text)
                    : ""
                  : props.object.type === "update"
                  ? props.object.data.title[0]
                    ? truncateText(props.object.data.title[0].text)
                    : ""
                  : props.object.type === "projec"
                  ? props.object.data.description[0]
                    ? truncateText(props.object.data.description[0].text)
                    : props.object.data.title[0].text
                  : props.object.data.subtitle
                  ? props.object.data.subtitle[0]
                    ? props.object.data.subtitle[0].text
                    : ""
                  : ""
              }
              fontSize={4}
              is="h1"
              mb={5}
            />
          </Box>
          <Button
            children={
              props.object.type === "member"
                ? props.object.lang === "en-us"
                  ? "Read more"
                  : props.object.lang === "es-mx"
                  ? "Lee mas"
                  : "اقرأ أكثر"
                : props.object.type === "projec"
                ? props.object.data.description[0]
                  ? props.object.lang === "en-us"
                    ? "Let's find out"
                    : props.object.lang === "es-mx"
                    ? "Vamos a averiguar"
                    : "هيا نكتشف"
                  : props.object.lang === "en-us"
                  ? "Learn more"
                  : props.object.lang === "es-mx"
                  ? "Aprende más"
                  : "أعرف أكثر"
                : props.object.lang === "en-us"
                ? "Learn more"
                : props.object.lang === "es-mx"
                ? "Aprende más"
                : "أعرف أكثر"
            }
            is="div"
            mt="auto"
            style={{ position: "relative" }}
          />
        </Container>
      </ThemeProvider>
    );
  } else {
    return null;
  }
};

Card.defaultProps = {
  to: "/",
  color: "yellow",
};

export default Card;



// WEBPACK FOOTER //
// ./src/components/Card.js